import React from 'react';
import { CelebrationDrawerFrame } from '@components/ui/celebration-drawer';
import { DrawerTitle } from '@components/ui/drawer';
import type { SelfReportedRegistration } from '@models/api';
import type { RegistrationCelebration } from '@utils/registrationCelebration';
import { GoingCelebrationIllustration } from './GoingCelebrationIllustration';
import { TicketQuestionAnswers, TicketQuestionText, TicketSite } from './TicketQuestionContent';
import { useTicketQuestion } from './useTicketQuestion';

interface TicketedGoingCelebrationDrawerProps {
  /** The server asked "have a ticket?" (TECH-575). */
  registration: RegistrationCelebration;
  /**
   * Arrival attribution of a post-auth Going (the stored intent's `?source=`), added to the view
   * event when present so per-source counts can combine it with event_going_welcome_viewed.
   */
  source?: string | null;
  /**
   * The member answered. The host saves it and closes, and returns true; it returns false for a tap
   * it refuses (its entrance veto), and the drawer then opens nothing and reports nothing. Called
   * first, synchronously in the tap. `onClose` stays the no-answer exit.
   */
  onAnswer: (answer: SelfReportedRegistration) => boolean;
  onClose: () => void;
}

/**
 * Post-"Going" celebration for a ticketed event (a claimed `registration_check` intent): the
 * going drawer's illustration, asking whether the member already has a ticket. Its own frame
 * around the shared ticket question: the eyebrow, the title, the illustration and, before the
 * event, the footnote.
 */
export const TicketedGoingCelebrationDrawer: React.FC<TicketedGoingCelebrationDrawerProps> = ({
  registration,
  source,
  onAnswer,
  onClose,
}) => {
  const question = useTicketQuestion({ registration, source, onAnswer });
  const { domain, live } = question;
  return (
    <CelebrationDrawerFrame
      // X, swipe, outside tap and Escape all land here: nothing is saved, the member stays Going.
      onOpenChange={open => {
        if (!open) onClose();
      }}
    >
      <div className="flex flex-col items-center gap-2.5 pt-2 text-center">
        <p className="font-livvic text-base text-mb-teal-ink">
          {live ? "You're here" : "You're going"}
        </p>
        <DrawerTitle className="font-livvic text-2xl font-bold text-black">
          {live ? 'Quick check at the door' : 'This event requires a ticket'}
        </DrawerTitle>
        <TicketQuestionText
          question={question}
          upcomingLead={
            <>
              Get your ticket on <TicketSite domain={domain} url={registration.registration_url} />.
            </>
          }
        />
      </div>
      {/* Decoration only; dropped on short screens so the question and both answers stay in reach. */}
      <div className="w-full px-2 pt-3 [@media(max-height:820px)]:hidden">
        <GoingCelebrationIllustration />
      </div>
      <div className="flex flex-col gap-3">
        <TicketQuestionAnswers question={question} />
        {live ? null : (
          <p className="px-2 pt-2 text-center font-fira text-xs leading-4 text-mb-dark-gray">
            You already tapped Going on meetball. You can connect with people at the event even if
            you&apos;re not there in person.
          </p>
        )}
      </div>
    </CelebrationDrawerFrame>
  );
};
